'use client';
import { useThemeProps } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { forwardRef } from 'react';
import Markdown from 'react-markdown';
import React from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';

export enum ChatBubbleVariant {
  AI = 'AI',
  User = 'User',
}

export interface ChatBubbleProps {
  time: string;
  message: any;
  variant?: ChatBubbleVariant;
  [key: string]: any;
}

interface ChatBubbleOwnerState extends ChatBubbleProps {
  variant?: ChatBubbleVariant;
}

const ChatBubbleRoot = styled('div', {
  name: 'MuiChatBubble',
  slot: 'root',
})<{ ownerState: ChatBubbleOwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  marginBottom: theme.spacing(1),
  marginRight: theme.spacing(1),
  flexDirection: 'column',
  ...(ownerState.message.role === 'MODEL' && {
    alignItems: 'flex-start',
  }),
  ...(ownerState.message.role === 'USER' && {
    alignItems: 'flex-end',
  }),
}));

const ChatMessage = styled('div', {
  name: 'MuiChatMessage',
  slot: 'root',
})<{ ownerState: ChatBubbleOwnerState }>(({ theme, ownerState }) => ({}));

const ChatTime = styled('div', {
  name: 'ChatTime',
  slot: 'root',
})<{ ownerState: ChatBubbleOwnerState }>(({ theme, ownerState }) => ({}));

function ReferencesCollapsible({
  references,
}: {
  references: { number: number; filename: string; page: number }[];
}) {
  const [open, setOpen] = React.useState(false);

  if (!references?.length) return null;

  return (
    <div style={{ marginTop: 16 }}>
      <Tooltip title={open ? 'Hide References' : 'Show References'}>
        <IconButton
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          aria-controls="references-list"
          size="small"
          sx={{
            marginBottom: 1,
          }}
        >
          <ExpandMoreIcon />
          <DescriptionIcon sx={{ ml: 1 }} />
        </IconButton>
      </Tooltip>
      <span style={{ fontWeight: 'bold', marginLeft: 8, verticalAlign: 'middle' }}>References</span>
      {open && (
        <ul id="references-list" style={{ margin: 0, paddingLeft: 20 }}>
          {references.map(ref => (
            <li key={ref.number}>
              <strong>[{ref.number}]</strong> {ref.filename}, page {ref.page}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const ChatBubble = forwardRef<HTMLDivElement, ChatBubbleProps>(function ChatBubble(inProps, ref) {
  const props = useThemeProps({ props: inProps, name: 'MuiChatBubble' });
  const { message, time, variant, ...other } = props;
  const ownerState: ChatBubbleOwnerState = {
    ...props,
    variant,
    time,
    message,
  };

  function formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'UTC',
    });
  }
  function arrayToMarkdown(texts: { title: string; content: string }[]): string {
    return texts
      .map(({ title, content }) => {
        const formattedContent =
          content.trim().toLowerCase() === 'insufficient data found.'
            ? content.trim()
            : content.trim();
        let section;
        if (title.toLowerCase() === 'overview') {
          section = `## Overview\n\n${formattedContent}`;
        } else {
          section = `**${title.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}**\n\n${formattedContent}`;
        }
        // Wrap the whole section in a blockquote, preserving line breaks
        return section
          .split('\n')
          .map(line => `> ${line}`)
          .join('\n');
      })
      .join('\n\n');
  }
  function formatRiskContent(content: string): string {
    // Wrap every line in a blockquote
    return content
      .split('\n')
      .map(line => `> ${line}`)
      .join('\n');
  }

  return (
    <ChatBubbleRoot ref={ref} ownerState={ownerState} {...other}>
      <ChatMessage ownerState={ownerState}>
        {message.content?.texts ? (
          <>
            <Markdown>{arrayToMarkdown(message.content.texts)}</Markdown>
            <ReferencesCollapsible references={message.content?.references || []} />
          </>
        ) : (
          <Markdown>{formatRiskContent(message.content)}</Markdown>
        )}
      </ChatMessage>
      <ChatTime ownerState={ownerState}>{formatTimestamp(message.created_date)}</ChatTime>
    </ChatBubbleRoot>
  );
});

export default ChatBubble;
export { ChatBubble };
